import React, { useMemo } from 'react';
import { useAITTheme } from '@this/shared/ui/theme';

export type SwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
};

export const Switch = ({ checked, onChange, disabled = false, size = 'small', label }: SwitchProps) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => {
    const dimensions =
      size === 'small'
        ? { width: 36, height: 20, knob: 16, translateX: 16 }
        : { width: 44, height: 24, knob: 20, translateX: 20 };

    return {
      wrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.utils.getSpacing(1),
        opacity: disabled ? 0.5 : 1
      },
      switch: {
        position: 'relative' as const,
        width: dimensions.width,
        height: dimensions.height,
        borderRadius: dimensions.height / 2,
        backgroundColor: checked ? theme.linkColor : theme.grayBorderColor,
        transition: 'background-color 0.2s ease',
        border: 'none',
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer'
      },
      knob: {
        position: 'absolute' as const,
        top: 2,
        left: 2,
        width: dimensions.knob,
        height: dimensions.knob,
        borderRadius: '50%',
        backgroundColor: '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transform: checked ? `translateX(${dimensions.translateX}px)` : 'translateX(0)',
        transition: 'transform 0.2s ease'
      },
      input: {
        position: 'absolute' as const,
        opacity: 0,
        pointerEvents: 'none' as const
      },
      label: {
        fontSize: '12px',
        color: theme.textColor
      }
    };
  }, [checked, disabled, size, theme]);

  const handleToggle = () => {
    if (disabled) return;
    onChange(checked === false);
  };

  return (
    <div style={styles.wrapper}>
      <button type="button" style={styles.switch} onClick={handleToggle} disabled={disabled}>
        <span style={styles.knob} />
        <input type="checkbox" checked={checked} readOnly style={styles.input} />
      </button>
      {label && <span style={styles.label}>{label}</span>}
    </div>
  );
};
